import { Badge } from "../../../components/ui/badge";
import type {
  BillingConfigStatus,
  InvoiceAddressBlock,
  InvoiceStatus,
  PaymentTrackingStatus,
  ProjectClientData,
} from "./types";

export const formatInvoiceAmount = (n: number) =>
  n.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

export const inr = (n: number) => `₹${formatInvoiceAmount(n)}`;

export const buildBillToFromProjectClient = (
  client: ProjectClientData
): InvoiceAddressBlock => {
  const lines: string[] = [];

  if (client.address?.trim()) {
    lines.push(client.address.trim());
  }
  if (client.mobile_number?.trim()) {
    lines.push(`Mobile: ${client.mobile_number.trim()}`);
  }
  if (client.email?.trim()) {
    lines.push(`Email: ${client.email.trim()}`);
  }

  return {
    name: client.client_name?.trim() || "—",
    lines,
    gstin: client.gst_no?.trim() ?? "",
  };
};

export const EMPTY_BILL_TO: InvoiceAddressBlock = {
  name: "",
  lines: ["Select a project to load client details"],
  gstin: "",
};

export interface ProjectGstDefaults {
  applicable: boolean;
  rate: string;
}

export const getProjectGstDefaults = (
  client?: ProjectClientData | null
): ProjectGstDefaults => {
  if (!client?.gst_types?.trim()) {
    return { applicable: false, rate: "" };
  }

  const applicable =
    client.gst_applicable !== undefined ? Boolean(client.gst_applicable) : true;

  const rate =
    client.gst_rate !== undefined &&
    client.gst_rate !== null &&
    String(client.gst_rate).trim() !== ""
      ? String(client.gst_rate)
      : "18";

  return { applicable, rate };
};

const INVOICE_STATUS_CLASSES: Record<InvoiceStatus, string> = {
  Paid: "bg-green-500/10 text-green-700 border-green-500/30",
  Sent: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  Overdue: "bg-red-500/10 text-red-700 border-red-500/30",
  Pending: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  Draft: "bg-muted text-muted-foreground border-border",
  "On Hold": "bg-secondary/10 text-secondary border-secondary/30",
};

const CONFIG_STATUS_CLASSES: Record<BillingConfigStatus, string> = {
  Active: "bg-green-500/10 text-green-700 border-green-500/30",
  Draft: "bg-muted text-muted-foreground border-border",
  "On Hold": "bg-secondary/10 text-secondary border-secondary/30",
};

export const invoiceStatusBadge = (status: InvoiceStatus) => (
  <Badge
    variant="outline"
    className={`${INVOICE_STATUS_CLASSES[status]} gap-1.5`}
  >
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {status}
  </Badge>
);

export const configStatusBadge = (status: BillingConfigStatus | string) => (
  <Badge
    variant="outline"
    className={`${CONFIG_STATUS_CLASSES[status as BillingConfigStatus] ?? CONFIG_STATUS_CLASSES.Draft} gap-1.5`}
  >
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {status}
  </Badge>
);

export const dashboardToneClass = (tone: string) =>
  ({
    info: "bg-blue-500/10 text-blue-600",
    warning: "bg-amber-500/10 text-amber-600",
    accent: "bg-secondary/10 text-secondary",
    success: "bg-green-500/10 text-green-600",
    destructive: "bg-red-500/10 text-red-600",
    primary: "bg-primary/10 text-primary",
  })[tone] ?? "bg-primary/10 text-primary";

export const invoiceStatusDotClass = (status: InvoiceStatus) => {
  switch (status) {
    case "Paid":
      return "bg-green-500";
    case "Sent":
      return "bg-blue-500";
    case "Overdue":
      return "bg-red-500";
    case "Pending":
      return "bg-amber-500";
    case "Draft":
      return "bg-muted-foreground";
    default:
      return "bg-secondary";
  }
};

export const paymentStatusDotClass = (status: PaymentTrackingStatus) => {
  switch (status) {
    case "Paid":
      return "bg-green-500";
    case "Overdue":
      return "bg-red-500";
    case "Pending":
      return "bg-blue-500";
    default:
      return "bg-muted-foreground";
  }
};
